const MAX_UNWRAP_DEPTH = 5;
const GSTATIC_FAVICON_HOST_RE = /^t\d+\.gstatic\.com$/i;
const COMMON_SECOND_LEVEL_TLDS = new Set([
  "co.uk",
  "org.uk",
  "gov.uk",
  "ac.uk",
  "com.au",
  "net.au",
  "org.au",
  "co.nz",
  "com.br",
  "com.mx",
  "co.jp",
  "ne.jp",
  "or.jp",
  "com.sg",
  "com.tr",
  "com.hk",
  "com.tw",
]);

const isHttpProtocol = (protocol: string) =>
  protocol === "http:" || protocol === "https:";

const isIPv4 = (host: string) => /^\d{1,3}(\.\d{1,3}){3}$/.test(host);

const extractRootDomainFromHost = (host: string): string => {
  const normalized = host.replace(/^(www\.)/i, "").toLowerCase();
  if (!normalized) return "";
  if (normalized === "localhost" || isIPv4(normalized)) return normalized;

  const parts = normalized.split(".").filter(Boolean);
  if (parts.length <= 2) return normalized;

  const lastTwo = `${parts[parts.length - 2]}.${parts[parts.length - 1]}`;
  if (COMMON_SECOND_LEVEL_TLDS.has(lastTwo) && parts.length >= 3) {
    return `${parts[parts.length - 3]}.${lastTwo}`;
  }

  return `${parts[parts.length - 2]}.${parts[parts.length - 1]}`;
};

const tryParseUrl = (value: string): URL | null => {
  try {
    return new URL(value);
  } catch {
    return null;
  }
};

const maybeDecode = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const normalizeCandidateValue = (value: string, key: string): string | null => {
  let candidate = value.trim();
  if (!candidate) return null;

  // Sometimes values are encoded once or twice.
  for (let i = 0; i < 2; i += 1) {
    const decoded = maybeDecode(candidate);
    if (decoded === candidate) break;
    candidate = decoded;
  }

  if (candidate.startsWith("//")) {
    candidate = `https:${candidate}`;
  }

  const parsedDirect = tryParseUrl(candidate);
  if (parsedDirect && isHttpProtocol(parsedDirect.protocol)) {
    return parsedDirect.toString();
  }

  if (key === "domain" || key === "domain_url") {
    const withScheme = `https://${candidate.replace(/^\/*/, "")}`;
    const parsedWithScheme = tryParseUrl(withScheme);
    if (parsedWithScheme && isHttpProtocol(parsedWithScheme.protocol)) {
      return parsedWithScheme.toString();
    }
  }

  return null;
};

const isGoogleFaviconProxyUrl = (url: URL): boolean => {
  const host = url.hostname.toLowerCase();
  const path = url.pathname.toLowerCase();

  const isGstaticShard = GSTATIC_FAVICON_HOST_RE.test(host);
  const isGoogleS2 =
    (host === "google.com" || host === "www.google.com") &&
    path.startsWith("/s2/favicons");

  if (isGoogleS2) return true;

  if (isGstaticShard && path.includes("faviconv2")) return true;

  // Conservative fallback for future shard/path variants.
  if ((host.endsWith(".gstatic.com") || host.endsWith("google.com")) && path.includes("favicon")) {
    return true;
  }

  return false;
};

export const normalizeSourceUrl = (raw: string | null | undefined): string | null => {
  if (!raw) return null;

  let candidate = raw.trim();
  if (!candidate) return null;

  if (candidate.startsWith("//")) {
    candidate = `https:${candidate}`;
  }

  for (let depth = 0; depth < MAX_UNWRAP_DEPTH; depth += 1) {
    const parsed = tryParseUrl(candidate);
    if (!parsed || !isHttpProtocol(parsed.protocol)) return null;

    if (!isGoogleFaviconProxyUrl(parsed)) {
      return parsed.toString();
    }

    const keys: Array<"url" | "domain_url" | "pageUrl" | "domain"> = [
      "url",
      "domain_url",
      "pageUrl",
      "domain",
    ];

    let unwrapped: string | null = null;

    for (const key of keys) {
      const value = parsed.searchParams.get(key);
      if (!value) continue;

      unwrapped = normalizeCandidateValue(value, key);
      if (unwrapped) break;
    }

    if (!unwrapped) {
      return null;
    }

    candidate = unwrapped;
  }

  const finalParsed = tryParseUrl(candidate);
  if (!finalParsed || !isHttpProtocol(finalParsed.protocol)) return null;
  if (isGoogleFaviconProxyUrl(finalParsed)) return null;

  return finalParsed.toString();
};

export const extractDisplayDomain = (raw: string | null | undefined): string => {
  const normalized = normalizeSourceUrl(raw);
  if (!normalized) return "";

  const parsed = tryParseUrl(normalized);
  if (!parsed) return "";

  return extractRootDomainFromHost(parsed.hostname);
};
